import { browserStorage, normalizeSelection, type SelectionStorage } from "@/lib/field-selection"
import { isRecord } from "@/lib/utils"
import { PULL_TO_MANY_MODES, type PullToManyModes } from "@/lib/pull-to-many"
import type { AtsPullRelatedEntity } from "@/types/ats-explorer-api"

/**
 * Pure helpers for remembering a pull's field selection in the browser: the last pull
 * of each entity, restored when the wizard opens on it again, and named presets the
 * user saves and applies. Both are per ATS source and entity, like the explorer's
 * columns, and both carry the to-many modes and related entities the fields depend on.
 */

const SELECTION_PREFIX = "ats-pull"
const PRESETS_PREFIX = "ats-pull-presets"

export interface PullSelection {
  /** Bare paths for a single-entity pull, prefixed keys for a composite one */
  fields: string[]
  toManyModes: PullToManyModes
  relatedEntities: AtsPullRelatedEntity[]
}

export interface StoredPullSelection extends PullSelection {
  savedAt: string
}

export interface PullSelectionPreset extends StoredPullSelection {
  name: string
}

export function pullSelectionStorageKey(atsSystem: string, atsSource: string, entityName: string): string {
  return `${SELECTION_PREFIX}:${atsSystem}:${atsSource}:${entityName}`
}

export function pullPresetsStorageKey(atsSystem: string, atsSource: string, entityName: string): string {
  return `${PRESETS_PREFIX}:${atsSystem}:${atsSource}:${entityName}`
}

/** The last pull's selection, or null when nothing valid is stored */
export function readStoredPullSelection(storage: SelectionStorage | null, key: string): StoredPullSelection | null {
  const parsed = readJson(storage, key)
  return isStoredSelection(parsed) ? normalize(parsed) : null
}

export function writeStoredPullSelection(storage: SelectionStorage | null, key: string, selection: PullSelection, now = new Date()): void {
  writeJson(storage, key, selection.fields.length === 0 ? null : { ...selection, savedAt: now.toISOString() })
}

/** The saved presets, invalid entries left out */
export function readPullPresets(storage: SelectionStorage | null, key: string): PullSelectionPreset[] {
  const parsed = readJson(storage, key)
  if (!Array.isArray(parsed)) return []
  return parsed.filter(isPreset).map((preset) => ({ ...normalize(preset), name: preset.name }))
}

export function writePullPresets(storage: SelectionStorage | null, key: string, presets: readonly PullSelectionPreset[]): void {
  writeJson(storage, key, presets.length === 0 ? null : presets)
}

/** The presets with one saved under `name`, replacing an existing preset of that name (case-insensitively) */
export function withPreset(presets: readonly PullSelectionPreset[], name: string, selection: PullSelection, now = new Date()): PullSelectionPreset[] {
  const trimmed = name.trim()
  return [...withoutPreset(presets, trimmed), { ...selection, name: trimmed, savedAt: now.toISOString() }]
}

export function withoutPreset(presets: readonly PullSelectionPreset[], name: string): PullSelectionPreset[] {
  return presets.filter((preset) => preset.name.toLowerCase() !== name.trim().toLowerCase())
}

/** Remembered pulls and presets outlive the tab */
export function localSelectionStorage(): SelectionStorage | null {
  return browserStorage("local")
}

function readJson(storage: SelectionStorage | null, key: string): unknown {
  try {
    const raw = storage?.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeJson(storage: SelectionStorage | null, key: string, value: unknown): void {
  try {
    if (value === null) storage?.removeItem(key)
    else storage?.setItem(key, JSON.stringify(value))
  } catch {
    // Storage can be unavailable (private mode, quota); the selection still applies for the session
  }
}

function normalize<T extends StoredPullSelection>(selection: T): T {
  return { ...selection, fields: normalizeSelection(selection.fields) }
}

function isStoredSelection(value: unknown): value is StoredPullSelection {
  if (!isRecord(value) || typeof value.savedAt !== "string") return false
  const fields = Array.isArray(value.fields) && value.fields.every((field) => typeof field === "string") ? normalizeSelection(value.fields) : []
  return fields.length > 0 && isModes(value.toManyModes) && Array.isArray(value.relatedEntities) && value.relatedEntities.every(isRelatedEntity)
}

function isPreset(value: unknown): value is PullSelectionPreset {
  if (!isStoredSelection(value)) return false
  const name: unknown = (value as Partial<PullSelectionPreset>).name
  return typeof name === "string" && name.trim() !== ""
}

function isModes(value: unknown): value is PullToManyModes {
  return isRecord(value) && Object.values(value).every((mode) => PULL_TO_MANY_MODES.includes(mode as never))
}

function isRelatedEntity(value: unknown): value is AtsPullRelatedEntity {
  return isRecord(value) && typeof value.name === "string" && typeof value.entityType === "string" && typeof value.referencePath === "string"
}

